import queryString from 'query-string';
import { endPoints } from 'config/routes';
import _ from 'lodash';
import { isDateInBetween, decodeB64ToObject, formatDate } from 'libs/common';
import config from 'config';

const { AUTH0: { namespace } } = config;

const AUTH_ACCESS_TOKEN = 'AUTH_ACCESS_TOKEN';
const AUTH_EXPIRES_AT = 'AUTH_EXPIRES_AT';

export function parseQueryString() {
  const query = window.appHistory &&
    window.appHistory.location &&
    (window.appHistory.location.search || window.appHistory.location.hash);
  return queryString.parse(query);
}

export function getUserCountryFromUrl() {
  const parsedQuery = parseQueryString();
  const countryCode = parsedQuery.country_code;
  return (countryCode && countryCode.toUpperCase()) || 'US';
}

export function getCouponIdFromUrl() {
  const parsedQuery = parseQueryString();
  const { coupon } = parsedQuery;
  return coupon;
}

export function getGoogleAuthCbParam() {
  const parsedQuery = parseQueryString();
  const encodedUserInfo = parsedQuery.state;
  if (encodedUserInfo) {
    const userInfo = decodeB64ToObject(encodedUserInfo);
    const { access_token, id_token, error_description } = parsedQuery;
    return {
      access_token,
      userInfo,
      id_token,
      error_description
    };
  }
  return false;
}

export function getActivationCodeFromUrl() {
  const parsedQuery = parseQueryString();
  const activationCode = parsedQuery.code || '';
  return activationCode;
}

export function getValuesFromUrl(params = []) {
  const urlValues = {};
  const parsedQuery = parseQueryString();
  for (let index = 0; index < params.length; index += 1) {
    const key = params[index];
    urlValues[params[index]] = parsedQuery[key] || '';
  }
  return urlValues;
}

export function getPlanIdFromUrl() {
  const parsedQuery = parseQueryString();
  const planId = parsedQuery.plan_id || '';
  return planId;
}

export function checkBetaUser() {
  const parsedQuery = parseQueryString();
  const { beta } = parsedQuery;
  return (beta === '1');
}

export function stringifyQueryString({ purgeKeys = [] }) {
  const parsedQuery = parseQueryString();
  purgeKeys.forEach(value => parsedQuery[value] && delete parsedQuery[value]);
  return queryString.stringify(parsedQuery);
}

export function parseHashString() {
  const hash = window.appHistory &&
    window.appHistory.location &&
    window.appHistory.location.hash;
  return queryString.parse(hash);
}

export function deleteAllCookies() {
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i += 1) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = `${name} =;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }
}

export function authCallback(authInfo, options = {}) {
  const { restrictRedirect = false } = options;
  saveToken(authInfo);
  const { appHistory } = window;
  const parsedQuery = parseQueryString();
  let { redirect, query } = parsedQuery || {};
  const { state } = parsedQuery || {};
  // to handle social login
  if (state) {
    const decodedObj = decodeB64ToObject(state);
    redirect = decodedObj.redirect;
    query = decodedObj.query;
  }

  if (!redirect) {
    redirect = endPoints.default;
    const { pathname } = window.location || {};
    if (pathname === endPoints.msSignin) {
      redirect = endPoints.products.ms.default;
    }
  } else {
    const decodedQuery = decodeB64ToObject(query);
    const stringifiedQuery = queryString.stringify(decodedQuery);
    if (stringifiedQuery) {
      redirect = `${redirect}?${stringifiedQuery}`;
    }
  }

  if (!restrictRedirect) {
    appHistory.replace(redirect);
  }
}

export function getToken() {
  return localStorage.getItem(AUTH_ACCESS_TOKEN);
}

export function getAccessTokenFromUrl() {
  const parsedQuery = parseQueryString();
  return parsedQuery.access_token;
}

export function getForceLoginParamFromUrl() {
  const parsedQuery = parseQueryString();
  return parsedQuery.force_login;
}

export function parseJwt(token) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace('-', '+').replace('_', '/');
  return JSON.parse(window.atob(base64));
}

export function saveToken(tokenDetails = {}) {
  localStorage.clear(); // clear the local storage
  // Set the time that the access token will expire at
  const expiresIn = tokenDetails.expiresIn || 86400;
  const expiresAt = JSON.stringify((expiresIn * 1000) + new Date().getTime());
  localStorage.setItem(AUTH_ACCESS_TOKEN, tokenDetails.accessToken);
  localStorage.setItem(AUTH_EXPIRES_AT, expiresAt);
}

export function processToken(accessToken) {
  const decodedToken = parseJwt(accessToken);
  saveToken({ accessToken, expiresIn: decodedToken.exp });
}

export function processUrlForAccessToken(location) {
  const accessToken = getAccessTokenFromUrl(location);
  // executes if token comes in url
  if (accessToken) {
    // removing token from route
    // saves token and expiry into local storage through a common helper method
    processToken(accessToken);
    const stringifiedQuery = stringifyQueryString({ purgeKeys: ['access_token'] });
    window.appHistory.replace(`${location.pathname}?${stringifiedQuery}`);
  }
}

export function processUrlForForceLogin(location = window.location) {
  const forceLogin = getForceLoginParamFromUrl(location);
  // executes if forceLogin comes in url
  if (forceLogin) {
    localStorage.clear();
    deleteAllCookies();
    const stringifiedQuery = stringifyQueryString({ purgeKeys: ['access_token', 'force_login'] });
    window.appHistory.replace(`${location.pathname}?${stringifiedQuery}`);
  }
}

export function isTokenUnavailable(location) {
  try {
    if (location) {
      processUrlForAccessToken(location);
    }
    const token = getToken();
    const tokenExpired = new Date().getTime() >= JSON.parse(localStorage.getItem(AUTH_EXPIRES_AT));
    return (!token || token === 'null' || tokenExpired);
  } catch (e) {
    return true;
  }
}

export function getUidFromToken() {
  try {
    if (!isTokenUnavailable()) {
      const token = getToken();
      const parsedToken = parseJwt(token);
      return parsedToken.sub.split('|').pop();
    }
  } catch (e) {
    // empty
  }
  return false;
}

export function getSocialLoginStatus(userId = '') {
  return userId.startsWith('google') || userId.startsWith('apple');
}

export function getUserPlanIds(userSubscriptions) {
  const planId = [];
  _.forEach(userSubscriptions, (value) => {
    if (isDateInBetween(value.startedAt, value.endsAt)) {
      planId.push(value.planId);
    }
  });
  return planId;
}

export function processAuthInfoForAdditionalInfo(authInfo = {}) {
  const { idToken, accessToken } = authInfo;
  const fields = getAdditionalInfoFromIdToken(idToken) || {};
  const isAdditionalInfoMissed = !checkAdditionalInfoCompleted(fields);
  return { fields, isAdditionalInfoMissed, accessToken };
}

export function getAdditionalInfoFromIdToken(idToken) {
  const info = parseJwt(idToken) || {};
  const { given_name = '', family_name = '' } = info;
  const isSignup = info[`${namespace}/is_signup`] || false;
  let birthdate = info[`${namespace}/birthdate`] || '';
  birthdate = formatDate(birthdate) || '';

  return {
    given_name, family_name, birthdate, isSignup
  };
}

export function checkAdditionalInfoCompleted(info = {}) {
  const { given_name = '', family_name = '', birthdate = '' } = info;
  if (!given_name || !family_name || !birthdate) {
    return false;
  }
  return true;
}



// WEBPACK FOOTER //
// ./src/libs/auth.js